import { sha256 } from "js-sha256"
import { Capacitor } from "@capacitor/core"
import { Device } from "@capacitor/device"
import { BlueIDAccess } from "@blueid/access-capacitor"
import { useOAuthService } from "@/services/OAuthService"
import { registerIdentityWithBackend, notifyBackendClaimed } from "@/controllers/BackboneAPI"
import { storeData, getStoredData, removeStoredItem } from "@/controllers/BaseController"
import Toastify from "toastify-js"
import "toastify-js/src/toastify.css"
import { getCurrentUserProfile, updateUserProfileTags } from "@/controllers/BaseController"

const STORE_KEYS = {
  IDENTITY: "blueid_identity",
  CLAIMED: "blueid_claimed_identity",
  NOTIFIED: "blueid_claimed_identity_notified"
}

const toastCache = new Set()
export function showToast(message, type = "default") {
  if (toastCache.has(message)) return
  toastCache.add(message)
  setTimeout(() => toastCache.delete(message), 2000)

  const background =
    {
      success: "#0f9d58",
      error: "#b00020",
      info: "#2196f3",
      warning: "#f9a825",
      default: "#333"
    }[type] || "#333"

  Toastify({
    text: message,
    duration: 3000,
    gravity: "bottom",
    position: "center",
    close: true,
    style: {
      background,
      color: "#fff",
      borderRadius: "8px",
      padding: "10px 18px",
      fontSize: "14px",
      fontWeight: "500",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
    }
  }).showToast()
}

export async function getDeviceFingerprint() {
  const info = await Device.getInfo()
  const { sub = "", email = "" } = await useOAuthService().getUserProfile()
  const { App } = await import("@capacitor/app").catch(() => ({}))
  const app = App ? await App.getInfo().catch(() => ({})) : {}

  const parts = [sub, email, info.platform, info.model, info.name, info.manufacturer, Capacitor.getPlatform()]
  const raw = parts.map(x => (x || "").toLowerCase()).join(":")
  const hash = await sha256(raw)
  return parseInt(hash.slice(0, 12), 16).toString(36).padStart(7, "0").slice(0, 7)
}

function updateTagObject(original, fingerprint, identity, extra = {}) {
  const updated = { ...identity, ...extra }
  return { ...original, [fingerprint]: JSON.stringify(updated) }
}

export async function hydrateIdentity() {
  const local = await getStoredData(STORE_KEYS.IDENTITY)
  if (local?.identityId && local?.activationToken) {
    return {
      source: "store",
      identity: local,
      claimed: (await getStoredData(STORE_KEYS.CLAIMED)) || false,
      notified: !!(await getStoredData(STORE_KEYS.NOTIFIED))?.["_id"]
    }
  }

  const fingerprint = await getDeviceFingerprint()
  const profile = await getCurrentUserProfile()
  const tags = profile?.tags || {}

  if (tags[fingerprint]) {
    try {
      const parsed = JSON.parse(tags[fingerprint])
      if (parsed.identityId && parsed.activationToken) {
        await storeData(STORE_KEYS.IDENTITY, parsed)
        await storeData(STORE_KEYS.CLAIMED, parsed.status === "CLAIMED")
        if (parsed.notified) await storeData(STORE_KEYS.NOTIFIED, { _id: parsed.identityId })
        return {
          source: "tags",
          identity: parsed,
          claimed: parsed.status === "CLAIMED",
          notified: !!parsed.notified
        }
      }
    } catch (e) {
      console.warn("⚠️ Failed to parse tag identity string:", tags[fingerprint])
    }
  }

  showToast("Registering identity...", "info")
  const identity = await registerIdentityWithBackend(fingerprint)
  await storeData(STORE_KEYS.IDENTITY, identity)

  const updatedTags = updateTagObject(tags, fingerprint, identity)
  await updateUserProfileTags(profile.id, updatedTags)
  console.log("✅ Identity registered and tag updated")
  return { source: "new", identity, claimed: false, notified: false }
}

export async function claimIdentityWithSDK(activationToken) {
  try {
    await BlueIDAccess.runCommand("claimIdentity", activationToken.trim())
    const [profile, fingerprint, identity] = await Promise.all([getCurrentUserProfile(), getDeviceFingerprint(), getStoredData(STORE_KEYS.IDENTITY)])

    await storeData(STORE_KEYS.CLAIMED, true)
    const updatedTags = updateTagObject(profile.tags || {}, fingerprint, identity, { status: "CLAIMED" })
    await updateUserProfileTags(profile.id, updatedTags)
    console.log("🔐 Identity claimed and tag updated")
    showToast("Identity claimed", "success")
  } catch (err) {
    showToast("Claim failed", "error")
    throw err
  }
}

export async function confirmBackendClaim() {
  const identity = await getStoredData(STORE_KEYS.IDENTITY)
  const notified = await getStoredData(STORE_KEYS.NOTIFIED)
  if (!identity?.identityId || notified?._id === identity.identityId) return

  try {
    await notifyBackendClaimed(identity.identityId)
    await storeData(STORE_KEYS.NOTIFIED, { _id: identity.identityId })

    const [profile, fingerprint] = await Promise.all([getCurrentUserProfile(), getDeviceFingerprint()])

    const updatedTags = updateTagObject(profile.tags || {}, fingerprint, identity, {
      status: "CLAIMED",
      notified: true
    })
    await updateUserProfileTags(profile.id, updatedTags)
    console.log("📣 Backend notified and tag updated")
    showToast("Claim confirmed", "success")
  } catch (err) {
    showToast("Notify failed", "error")
    throw err
  }
}

export async function addIdentityWithSDK() {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    if (!identity?.identityId) throw new Error("Missing identity data")

    const { identities = [] } = (await BlueIDAccess.runCommand("getIdentities")) || {}
    if (identities.some(i => i.identityId === identity.identityId)) {
      showToast("ℹ️ Identity already exists in SDK.", "info")
      return { alreadyExists: true }
    }

    const result = await BlueIDAccess.runCommand("addIdentity", identity)
    showToast("✅ Identity added via SDK.", "success")
    return result
  } catch (err) {
    showToast("❌ Failed to add identity via SDK.", "error")
    throw err
  }
}

export async function synchronizeIdentity() {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    await BlueIDAccess.runCommand("synchronizeIdentity", identity.identityId, false)
    showToast("🔄 Identity synchronized.", "success")
  } catch (err) {
    showToast("❌ Failed to synchronize identity.", "error")
    throw err
  }
}

export async function getAccessObjects() {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    const { objects = [] } = (await BlueIDAccess.runCommand("getAccessObjects", identity.identityId)) || {}
    const filtered = objects.filter(o => Array.isArray(o.deviceIds) && o.deviceIds.length > 0)
    console.log("[BlueID] Final access objects list (raw):", filtered)
    showToast(`🔓 Found ${filtered.length} access object(s)`, "info")
    return filtered
  } catch (err) {
    console.error("[BlueID] Failed to fetch access objects:", err)
    showToast("❌ Failed to get access objects.", "error")
    return []
  }
}

export async function tryUnlockBlueIDDevice(deviceId) {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    const result = await BlueIDAccess.runCommand("tryAccessDevice", identity.identityId, deviceId)
    return { success: !!result, result }
  } catch (err) {
    showToast("❌ Unlock failed.", "error")
    return { success: false, error: err.message || "Unknown error" }
  }
}

export async function getIdentitiesFromSDK() {
  try {
    const result = await BlueIDAccess.runCommand("getIdentities")
    return result?.identities || []
  } catch (err) {
    showToast("❌ Failed to fetch identities from SDK.", "error")
    return []
  }
}

export async function resetBlueIDState() {
  await removeStoredItem(STORE_KEYS.IDENTITY)
  await removeStoredItem(STORE_KEYS.CLAIMED)
  await removeStoredItem(STORE_KEYS.NOTIFIED)
  //showToast('🧹 BlueID state reset.', 'info')
}
